import { useEffect, useState } from 'react'
import { ListChecks } from 'lucide-react'
import DatePicker from '../../components/DatePicker'
import { FormDrawer } from '../../components/Drawer'
import { FormBody, FormCols, InfoNote, SwitchField, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import SegmentedControl from '../../components/SegmentedControl'
import TextField from '../../components/TextField'
import { SURVEY_TYPE, findSurveyFor, getSurvey, saveSurvey } from '../engagement/engagementService'
import SurveyQuestionsEditor from '../engagement/SurveyQuestionsEditor'
import { surveysStrings } from '../engagement/strings'
import { strings as S } from './strings'

const D = S.detail.surveyPanel

/** The legacy's three, in its order — `surveyStp1..3` in Components/SurveyModal. */
const STEPS = ['info', 'properties', 'questions']

const EMPTY = {
  id: 0,
  title: '',
  titleEn: '',
  descriptionAr: '',
  descriptionEn: '',
  enabled: false,
  mandatory: false,
  timeRestricted: false,
  startDate: '',
  endDate: '',
  restriction: null,
  groupIds: [],
  questions: [],
}

/**
 * استبيان البرنامج — the legacy's survey modal, opened from the programme's own page.
 *
 * The card used to navigate to the surveys screen, which is not what the legacy does and
 * loses the programme on the way. `showSurveyModalForType('program', id)` asks
 * `CheckSurveyForType` whether this programme already has a survey and opens the SAME modal
 * in place — for create or for edit — with the type and the target already decided. So
 * neither is a field here: it is this programme's survey, which is why the drawer opened.
 *
 * The three steps are the legacy's own: what the survey IS, how it behaves, and its
 * questions. `SaveSurveyCommand` carries the questions and their answers along with the
 * survey, so all three leave in one call.
 */
export default function ProgramSurveyDrawer({ open, program, groups, onClose, onSaved }) {
  const [values, setValues] = useState(EMPTY)
  const [step, setStep] = useState(0)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !program?.id) return undefined

    let cancelled = false
    setStep(0)
    setErrors({})
    setLoading(true)

    /* Create or edit, decided the way the legacy decides it. */
    findSurveyFor('program', program.id)
      .then(async (surveyId) => {
        const existing = surveyId > 0 ? await getSurvey(surveyId).catch(() => null) : null
        if (cancelled) return
        setValues(existing ? { ...EMPTY, ...existing, id: surveyId } : { ...EMPTY })
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setValues({ ...EMPTY })
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, program])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  /* What `SaveSurveyValidator` will refuse, asked here first: both names AND both
     descriptions, and at least one question. The descriptions were not asked for, so a
     survey left without them was rejected by the server after three steps of work. */
  const validateStep = () => {
    const next = {}
    if (step === 0) {
      if (!values.title.trim()) next.title = S.form.required
      if (!values.titleEn.trim()) next.titleEn = S.form.required
      if (values.descriptionAr.trim().length < 2) next.descriptionAr = S.form.required
      if (values.descriptionEn.trim().length < 2) next.descriptionEn = S.form.required
    }
    if (step === 1 && values.timeRestricted) {
      if (!values.startDate) next.startDate = S.form.required
      if (!values.endDate) next.endDate = S.form.required
    }
    if (step === 2 && values.questions.length === 0) next.questions = surveysStrings.form.questions.noQuestionsYet
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!validateStep()) return

    /* The steps run in order, as the legacy's stepper does. */
    if (step < STEPS.length - 1) {
      setStep(step + 1)
      return
    }

    setSaving(true)
    const outcome = await saveSurvey({ ...values, type: SURVEY_TYPE.program, programId: program.id })
    setSaving(false)
    onSaved(outcome)
  }

  return (
    <FormDrawer
      open={open}
      size="lg"
      formId="program-survey"
      icon={ListChecks}
      title={D.title}
      subtitle={program?.name}
      submitLabel={step < STEPS.length - 1 ? D.next : S.form.save}
      cancelLabel={S.close}
      submitting={saving || loading}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormBody>
        <SegmentedControl
          items={STEPS.map((id, index) => ({ id, label: `${index + 1}. ${D.steps[id]}` }))}
          value={STEPS[step]}
          onChange={(id) => setStep(STEPS.indexOf(id))}
          ariaLabel={D.title}
        />

        {loading ? (
          <p className="pd__survey-note">{D.loading}</p>
        ) : (
          <>
            {step === 0 && (
              <>
              <FormCols>
                <FormRow label={D.nameAr} error={errors.title}>
                  {(id) => (
                    <TextField
                      id={id}
                      value={values.title}
                      onChange={set('title')}
                      invalid={!!errors.title}
                    />
                  )}
                </FormRow>
                <FormRow label={D.nameEn} error={errors.titleEn}>
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values.titleEn}
                      onChange={set('titleEn')}
                      invalid={!!errors.titleEn}
                    />
                  )}
                </FormRow>
                <FormRow label={D.descriptionAr} error={errors.descriptionAr}>
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={3}
                      value={values.descriptionAr}
                      onChange={set('descriptionAr')}
                      invalid={!!errors.descriptionAr}
                    />
                  )}
                </FormRow>
                <FormRow label={D.descriptionEn} error={errors.descriptionEn}>
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={3}
                      dir="ltr"
                      value={values.descriptionEn}
                      onChange={set('descriptionEn')}
                      invalid={!!errors.descriptionEn}
                    />
                  )}
                </FormRow>
              </FormCols>
              {/* ENABLED LIVES ON STEP ONE, where the legacy keeps it — `chkBxActiveSurvey`
                  is inside `#surveyInfo`, not `#surveyProperties`. It is part of what the
                  survey IS, not of how it behaves. */}
              <SwitchField
                checked={values.enabled}
                onChange={set('enabled')}
                label={D.enabled}
                hint={D.enabledHint}
              />
              </>
            )}

            {step === 1 && (
              <>
                {/* Neither the type nor the target is asked for — that is the whole point of
                    opening this from the programme rather than from the surveys screen. */}
                <InfoNote>{D.targetNote}</InfoNote>

                <SwitchField
                  checked={values.mandatory}
                  onChange={set('mandatory')}
                  label={D.mandatory}
                />
                <SwitchField
                  checked={values.timeRestricted}
                  onChange={set('timeRestricted')}
                  label={D.timeRestricted}
                />

                {values.timeRestricted && (
                  <FormCols>
                    <FormRow label={D.startDate} error={errors.startDate}>
                      {(id) => (
                        <DatePicker id={id} value={values.startDate} onChange={set('startDate')} />
                      )}
                    </FormRow>
                    <FormRow label={D.endDate} error={errors.endDate}>
                      {(id) => (
                        <DatePicker id={id} value={values.endDate} onChange={set('endDate')} />
                      )}
                    </FormRow>
                  </FormCols>
                )}

                <GroupPicker
                  options={groups}
                  selected={values.groupIds ?? []}
                  onChange={(next) => set('groupIds')(next.map(Number))}
                  legend={D.groups}
                  selectAllLabel={D.selectAll}
                  filterLabel={D.filter}
                />
              </>
            )}

            {step === 2 && (
              <SurveyQuestionsEditor
                questions={values.questions}
                onChange={(questions) => {
                  setValues((previous) => ({ ...previous, questions }))
                  setErrors((previous) => ({ ...previous, questions: undefined }))
                }}
                error={errors.questions}
              />
            )}
          </>
        )}
      </FormBody>
    </FormDrawer>
  )
}
